import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skel, thisMonth } from "./shared";

const stat = (t) => (t.done ? "Done" : t.overdue ? "Overdue" : t.status || "Open");
const TONE = { Done: "text-emerald-700", Overdue: "text-red-700" };

function Timeline({ groups }) {
  const all = groups.flatMap(([, ts]) => ts).filter(t => t.due);
  if (!all.length) return <div className="text-xs text-slate-500 p-4">No dated tasks.</div>;
  const months = [...new Set(all.map(t => t.due.slice(0, 7)))].sort();
  return (
    <div className="overflow-x-auto" data-testid="mon-tasks-timeline"><table className="text-xs min-w-full">
      <thead><tr><th className="text-left px-3 py-2 w-72">Task</th>{months.map(m => <th key={m} className="px-2 font-mono text-slate-500">{m}</th>)}</tr></thead>
      <tbody>{all.map(t => <tr key={t.id} className="border-t"><td className="px-3 py-1.5 truncate max-w-[280px]">{t.title}</td>
        {months.map(m => <td key={m} className="px-1">{t.due.slice(0, 7) === m && <div className={`h-3 rounded ${t.done ? "bg-emerald-500" : t.overdue ? "bg-red-500" : "bg-blue-500"}`} title={t.due} />}</td>)}</tr>)}</tbody>
    </table></div>
  );
}

export default function TasksTab({ data, owner, month }) {
  const [view, setView] = useState("list");
  const [flt, setFlt] = useState("all");
  const groups = useMemo(() => {
    const m = month || thisMonth();
    const ts = (data?.tasks || []).filter(t => (owner === "all" || t.assignee_id === owner)
      && (flt === "all" || (flt === "overdue" && t.overdue) || (flt === "month" && (t.due || "").startsWith(m))));
    const g = {};
    ts.forEach(t => { (g[t.parent_code || "Other"] ||= []).push(t); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [data, owner, flt, month]);
  if (!data) return <Skel rows={6} />;
  return (
    <Card data-testid="mon-tasks"><CardContent className="p-0">
      <div className="flex flex-wrap gap-2 p-3 border-b">
        {[["all", "All"], ["overdue", "Overdue"], ["month", "Due this month"]].map(([k, l]) => <Button key={k} size="sm" variant={flt === k ? "default" : "outline"} onClick={() => setFlt(k)} data-testid={`mon-tasks-f-${k}`}>{l}</Button>)}
        <div className="ml-auto flex gap-1">{[["list", "List"], ["timeline", "Timeline"]].map(([k, l]) => <Button key={k} size="sm" variant={view === k ? "secondary" : "ghost"} onClick={() => setView(k)} data-testid={`mon-tasks-view-${k}`}>{l}</Button>)}</div>
      </div>
      {view === "timeline" ? <Timeline groups={groups} /> : groups.length === 0 ? <div className="text-sm text-slate-500 p-6 text-center" data-testid="mon-tasks-empty">No tasks match.</div> : groups.map(([code, ts]) => (
        <div key={code} className="border-b last:border-0">
          <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-600">{code === "Other" ? "Not linked to an output" : `Output ${code}`} · {ts.length}</div>
          {ts.map(t => <div key={t.id} className="grid grid-cols-12 gap-2 px-4 py-2 text-sm border-t border-slate-100" data-testid={`mon-task-${t.id}`}>
            <span className="col-span-12 md:col-span-6 truncate">{t.code ? <span className="font-mono text-xs text-slate-500 mr-1">{t.code}</span> : null}{t.title}</span>
            <span className="col-span-5 md:col-span-3 text-xs text-slate-600">{t.owner_name || "No owner"}</span>
            <span className="col-span-4 md:col-span-2 text-xs font-mono">{t.due || "—"}</span>
            <span className={`col-span-3 md:col-span-1 text-xs ${TONE[stat(t)] || "text-slate-600"}`}>{stat(t)}</span>
          </div>)}
        </div>))}
    </CardContent></Card>
  );
}
